import type { TabularLogMapping } from './api';

type CurveKey = keyof TabularLogMapping['columns'];

type MappingField = {
  key: CurveKey;
  label: string;
  aliases: string[];
  units: Array<TabularLogMapping['units'][keyof TabularLogMapping['units']]>;
};

export const tabularMappingFields: MappingField[] = [
  { key: 'depth_m', label: 'Measured depth', aliases: ['depth', 'dept', 'md', 'measureddepth', 'measureddepthft', 'measureddepthm'], units: ['m', 'ft'] },
  { key: 'natural_gamma', label: 'Natural gamma', aliases: ['gr', 'gamma', 'naturalgamma', 'gammacount'], units: ['API', 'GAPI'] },
  { key: 'resistivity', label: 'Resistivity', aliases: ['rt', 'res', 'resistivity', 'deepresistivity', 'ild', 'lld'], units: ['ohm.m'] },
  { key: 'spontaneous_potential', label: 'Spontaneous potential', aliases: ['sp', 'ssp', 'spontaneouspotential', 'spvolts'], units: ['mV', 'V'] },
  { key: 'caliper', label: 'Caliper', aliases: ['cali', 'cal', 'caliper', 'holesize', 'holesizemm'], units: ['in', 'mm'] },
];

export function normaliseTabularHeader(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

export function detectTabularDelimiter(line: string): TabularLogMapping['delimiter'] {
  if (line.includes('\t')) return '\t';
  return line.includes(';') && !line.includes(',') ? ';' : ',';
}

export function suggestTabularMapping(headers: string[], delimiter: TabularLogMapping['delimiter']): TabularLogMapping {
  const findHeader = (aliases: string[]) => headers.find((header) => {
    const normalised = normaliseTabularHeader(header);
    return aliases.some((alias) => normalised === alias || normalised.includes(alias));
  }) ?? '';
  const columns = tabularMappingFields.reduce((result, field) => {
    result[field.key] = findHeader(field.aliases);
    return result;
  }, {} as TabularLogMapping['columns']);
  const depthHeader = normaliseTabularHeader(columns.depth_m);
  const spHeader = normaliseTabularHeader(columns.spontaneous_potential);
  const caliperHeader = normaliseTabularHeader(columns.caliper);
  return {
    delimiter,
    columns,
    units: {
      depth_m: depthHeader.endsWith('ft') ? 'ft' : 'm',
      natural_gamma: 'API',
      resistivity: 'ohm.m',
      spontaneous_potential: spHeader.includes('volt') ? 'V' : 'mV',
      caliper: caliperHeader.endsWith('mm') ? 'mm' : 'in',
    },
  };
}
